import { createContext, useContext, useEffect, useMemo, ReactNode } from 'react';
import { useClerkGate } from '@/hooks/useClerkGate';
import { setUserContext } from '@/lib/sentry';

/**
 * Authentication Context - Single Responsibility: User authentication state
 * Separated from RoleContext to follow SOLID principles
 *
 * Derives state from useClerkGate primitives (NOT Clerk's useAuth /
 * sessionClaims): the claims object gets a new identity on every ~50s token
 * refresh, which used to push a fresh user object through this context and
 * re-render every consumer — part of the app-wide token-refresh render storm.
 */

interface AuthUser {
  userId: string;
  email: string;
  name: string | null;
}

type AuthState =
  | { status: 'initializing' } // Waiting for Clerk
  | { status: 'unauthenticated' } // Not signed in
  | { status: 'authenticated'; user: AuthUser }; // Authenticated with user data

interface AuthContextValue {
  authState: AuthState;
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, userId, email, name } = useClerkGate();

  const authState = useMemo<AuthState>(() => {
    if (!isLoaded) return { status: 'initializing' };
    if (!isSignedIn || !userId) return { status: 'unauthenticated' };
    return {
      status: 'authenticated',
      user: { userId, email: email ?? '', name },
    };
  }, [isLoaded, isSignedIn, userId, email, name]);

  // Sentry user context — side effect only, keyed on the derived state.
  // Cleared on sign-out so later events aren't attributed to the previous
  // user (matters for in-place transitions, e.g. remote session revocation).
  useEffect(() => {
    if (authState.status === 'authenticated') {
      const { userId, email, name } = authState.user;
      setUserContext(userId, email, name);
    } else if (authState.status === 'unauthenticated') {
      setUserContext(null, null, null);
    }
  }, [authState]);

  const value = useMemo<AuthContextValue>(
    () => ({
      authState,
      user: authState.status === 'authenticated' ? authState.user : null,
      isAuthenticated: authState.status === 'authenticated',
      isLoading: authState.status === 'initializing',
      // Kept for interface compatibility (RoleAdapter/EmbeddedRoleProvider
      // read it); no error state is reachable from useClerkGate primitives.
      error: null,
    }),
    [authState]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuthUser = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuthUser must be used within AuthProvider');
  return context;
};
